import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { useAuth } from '../providers/AuthProvider';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { PotencySummary } from '../components/recipes/PotencySummary';
import { calculatePotency } from '../utils/potency';
import { Recipe, InfusedIngredientInput } from '../types/recipe';
import { getRecipe, deleteRecipe } from '../graphql/recipes';

const client = generateClient();

export function RecipeDetailPage() {
  const { recipeId } = useParams<{ recipeId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [adjustedServings, setAdjustedServings] = useState<number>(1);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    async function fetchRecipe() {
      if (!user?.userId || !recipeId) return;

      setLoading(true);
      setError(null);
      setNotFound(false);

      try {
        const res = await client.graphql({
          query: getRecipe,
          variables: { userId: user.userId, recipeId },
        });

        const data = (res as { data: { getRecipe: Recipe | null } }).data.getRecipe;

        if (!data) {
          setNotFound(true);
        } else {
          setRecipe(data);
          setAdjustedServings(data.servings);
        }
      } catch {
        setError('Failed to load recipe. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    fetchRecipe();
  }, [user?.userId, recipeId]);

  const infusedIngredients: InfusedIngredientInput[] = useMemo(() => {
    if (!recipe) return [];
    return recipe.ingredients
      .filter((ing) => ing.isCannabisInfused)
      .map((ing) => ({
        materialWeight: ing.materialWeight ?? 0,
        cannabinoidPercentage: ing.cannabinoidPercentage ?? 0,
        decarboxylationEfficiency: ing.decarboxylationEfficiency ?? 0,
        extractionEfficiency: ing.extractionEfficiency ?? 0,
        cannabinoidType: ing.cannabinoidType ?? '',
        name: ing.name,
      }));
  }, [recipe]);

  const potencyResult = useMemo(() => {
    return calculatePotency(infusedIngredients, adjustedServings);
  }, [infusedIngredients, adjustedServings]);

  const handleServingsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 1 && value <= 999) {
      setAdjustedServings(value);
    }
  };

  const handleDelete = async () => {
    if (!user?.userId || !recipeId) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      await client.graphql({
        query: deleteRecipe,
        variables: { input: { userId: user.userId, recipeId } },
      });
      navigate('/recipes');
    } catch {
      setDeleteError('Failed to delete recipe. Please try again.');
      setShowDeleteDialog(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <LoadingSpinner />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="page">
        <p>Recipe not found. It may have been deleted or does not exist.</p>
        <Link to="/recipes" className="cta-button" style={{ marginTop: '1rem', display: 'inline-block' }}>
          Back to Recipes
        </Link>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <ErrorMessage message={error} />
      </div>
    );
  }

  if (!recipe) return null;

  const servingsChanged = adjustedServings !== recipe.servings;

  return (
    <div className="page recipe-detail-page">
      <div className="recipe-detail-header">
        <h2>{recipe.title}</h2>
        <div className="recipe-detail-actions">
          <button
            className="cta-button"
            onClick={() => navigate(`/recipes/${recipeId}/edit`)}
          >
            Edit
          </button>
          <button
            className="cta-button cta-button-danger"
            onClick={() => setShowDeleteDialog(true)}
          >
            Delete
          </button>
        </div>
      </div>

      {recipe.description && (
        <p className="recipe-detail-description">{recipe.description}</p>
      )}

      <div className="recipe-detail-servings">
        <label htmlFor="serving-adjustment">
          <strong>Servings:</strong>
        </label>
        <input
          id="serving-adjustment"
          type="number"
          min={1}
          max={999}
          value={adjustedServings}
          onChange={handleServingsChange}
          className="serving-input"
        />
        {servingsChanged && (
          <span className="servings-comparison">
            (original: {recipe.servings})
          </span>
        )}
      </div>

      <section className="recipe-detail-section">
        <h3>Ingredients</h3>
        <ul className="recipe-ingredients-list">
          {recipe.ingredients.map((ing, index) => (
            <li key={index} className={`recipe-ingredient-item ${ing.isCannabisInfused ? 'infused' : ''}`}>
              <span className="ingredient-info">
                {ing.amount} {ing.unit} — {ing.name}
              </span>
              {ing.isCannabisInfused && (
                <span className="infused-label">
                  🌿 {ing.cannabinoidType}{' '}
                  ({potencyResult.byIngredient.find((p) => p.ingredientName === ing.name)?.mg.toFixed(2) ?? '0.00'} mg)
                </span>
              )}
            </li>
          ))}
        </ul>
      </section>

      {recipe.instructions && recipe.instructions.length > 0 && (
        <section className="recipe-detail-section">
          <h3>Instructions</h3>
          <ol className="recipe-instructions-list">
            {recipe.instructions.map((step, index) => (
              <li key={index}>{step}</li>
            ))}
          </ol>
        </section>
      )}

      <section className="recipe-detail-section">
        <PotencySummary potencyResult={potencyResult} servings={adjustedServings} />
      </section>

      {deleteError && (
        <div className="recipe-detail-error">
          <ErrorMessage message={deleteError} />
        </div>
      )}

      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Recipe"
        message={`Are you sure you want to delete "${recipe.title}"? This action cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteDialog(false)}
      />

      {deleting && <LoadingSpinner />}
    </div>
  );
}
